/**
 * Hat pack builder
 * Namespaced hat-mix so it is easy to spot after a theme update.
 * Talks to the Sleek/Glint cart drawer through the theme's own events.
 */
class HatMixBuilder extends HTMLElement {
  /* connectedCallback is written to be safe to run more than once, so the
     element still works if the theme editor or any script moves it. */
  connectedCallback() {
    this.max = parseInt(this.dataset.max, 10) || 3;
    this.freeEvery = parseInt(this.dataset.freeEvery, 10) || 3;

    if (!Array.isArray(this.slots) || this.slots.length !== this.max) {
      this.slots = new Array(this.max).fill(null);
    }
    this.busy = false;

    this.strings = this.readStrings();
    this.tray = this.querySelector('[data-hat-tray]');
    this.slotEls = Array.from(this.querySelectorAll('[data-hat-slot]'));
    this.addBtn = this.querySelector('[data-hat-add]');
    this.addLabel = this.querySelector('[data-hat-add-label]');
    this.nudgeEl = this.querySelector('[data-hat-nudge]');
    this.totalEl = this.querySelector('[data-hat-total]');
    this.wasEl = this.querySelector('[data-hat-was]');
    this.errorEl = this.querySelector('[data-hat-error]');

    if (!this._onClick) this._onClick = this.onClick.bind(this);
    this.removeEventListener('click', this._onClick);
    this.addEventListener('click', this._onClick);

    this.watchCartDrawer();
    this.watchTrayHeight();
    this.render();
  }

  disconnectedCallback() {
    if (this._onClick) this.removeEventListener('click', this._onClick);
    if (this.drawerObserver) this.drawerObserver.disconnect();
    if (this.trayObserver) this.trayObserver.disconnect();
  }

  readStrings() {
    const el = this.querySelector('[data-hat-strings]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  /* --------------------------------------------------------- interaction */

  onClick(event) {
    const pick = event.target.closest('[data-hat-pick]');
    if (pick && this.contains(pick)) {
      event.preventDefault();
      this.pick(pick);
      return;
    }

    const remove = event.target.closest('[data-hat-remove]');
    if (remove && this.contains(remove)) {
      event.preventDefault();
      this.removeAt(parseInt(remove.dataset.hatRemove, 10));
      return;
    }

    const add = event.target.closest('[data-hat-add]');
    if (add && this.contains(add)) {
      event.preventDefault();
      this.submit();
    }
  }

  pick(button) {
    if (button.disabled) return;

    const index = this.slots.indexOf(null);
    if (index === -1) {
      this.flashTray();
      this.setNudge(this.strings.tray_full || 'Your pack is full.');
      return;
    }

    this.slots[index] = {
      id: button.dataset.variantId,
      price: parseInt(button.dataset.price, 10) || 0,
      title: button.dataset.productTitle || '',
      colour: button.dataset.colour || '',
      image: button.dataset.image || ''
    };

    this.hideError();
    this.render();
    this.announceSlot(index);
  }

  removeAt(index) {
    if (Number.isNaN(index) || index < 0 || index >= this.slots.length) return;
    this.slots[index] = null;
    this.hideError();
    this.render();
  }

  /* -------------------------------------------------------------- render */

  render() {
    this.slotEls.forEach((slotEl, i) => {
      const item = this.slots[i];
      const img = slotEl.querySelector('.hat-mix__slot-image');
      const removeBtn = slotEl.querySelector('.hat-mix__slot-remove');

      slotEl.classList.toggle('is-filled', Boolean(item));

      if (item) {
        if (item.image) {
          img.src = item.image;
          img.alt = [item.title, item.colour].filter(Boolean).join(' in ');
          img.hidden = false;
        } else {
          img.hidden = true;
        }
        removeBtn.hidden = false;
        slotEl.title = [item.title, item.colour].filter(Boolean).join(' in ');
      } else {
        img.hidden = true;
        img.removeAttribute('src');
        removeBtn.hidden = true;
        slotEl.removeAttribute('title');
      }
    });

    const picked = this.slots.filter(Boolean);
    const count = picked.length;
    const subtotal = picked.reduce((sum, item) => sum + item.price, 0);
    const freeCount = Math.floor(count / this.freeEvery);
    const cheapest = picked.map((item) => item.price).sort((a, b) => a - b);
    const discount = cheapest.slice(0, freeCount).reduce((sum, price) => sum + price, 0);
    const total = subtotal - discount;

    this.totalEl.textContent = count ? this.money(total) : '';
    if (discount > 0) {
      this.wasEl.textContent = this.money(subtotal);
      this.wasEl.hidden = false;
    } else {
      this.wasEl.hidden = true;
    }

    this.setNudge(this.nudgeFor(count, subtotal, total));

    this.addBtn.disabled = count === 0 || this.busy;
    this.addLabel.textContent =
      count === 0
        ? this.strings.button_label_empty || 'Pick your hats'
        : this.strings.button_label || 'Add to cart';

    this.classList.toggle('hat-mix--has-picks', count > 0);
  }

  nudgeFor(count, subtotal, total) {
    const left = this.freeEvery - count;
    let template;

    if (count === 0) template = this.strings.nudge_empty;
    else if (left === 1) template = this.strings.nudge_almost;
    else if (left > 1) template = this.strings.nudge_partial;
    else template = this.strings.nudge_full;

    if (!template) return '';

    // One away from the free hat, the honest number is what they pay right now,
    // because adding the third hat does not change it.
    const shownTotal = left === 1 ? subtotal : total;

    return template
      .replace(/\[total\]/g, this.money(shownTotal))
      .replace(/\[was\]/g, this.money(subtotal))
      .replace(/\[left\]/g, String(Math.max(left, 0)))
      .replace(/\[count\]/g, String(count));
  }

  setNudge(text) {
    if (this.nudgeEl) this.nudgeEl.textContent = text || '';
  }

  announceSlot(index) {
    const slotEl = this.slotEls[index];
    if (!slotEl) return;
    slotEl.classList.remove('just-filled');
    void slotEl.offsetWidth;
    slotEl.classList.add('just-filled');
  }

  flashTray() {
    if (!this.tray) return;
    this.tray.classList.remove('is-flashing');
    void this.tray.offsetWidth;
    this.tray.classList.add('is-flashing');
  }

  /* ---------------------------------------------------------- add to cart */

  async submit() {
    const picked = this.slots.filter(Boolean);
    if (!picked.length || this.busy) return;

    this.busy = true;
    this.addBtn.disabled = true;
    this.addBtn.classList.add('btn--loading');
    this.hideError();

    // The same hat picked twice becomes one line with quantity two.
    const byVariant = new Map();
    picked.forEach((item) => {
      byVariant.set(item.id, (byVariant.get(item.id) || 0) + 1);
    });
    const items = Array.from(byVariant, (entry) => ({ id: entry[0], quantity: entry[1] }));

    const countBefore = await this.cartCount();

    try {
      const response = await fetch(this.routes.cart_add_url, Object.assign({}, this.fetchConfig(), {
        body: JSON.stringify({ items: items })
      }));

      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        data = {};
      }

      if (!response.ok || data.status) {
        /* Shopify adds whatever it can and STILL returns 422. Asking the cart
           what actually landed is the only way to avoid showing a pack that is
           already partly in the cart, which is how people double add. */
        const countAfter = await this.cartCount();
        const landed =
          typeof countBefore === 'number' && typeof countAfter === 'number' && countAfter > countBefore;

        this.showError(this.errorMessage(data));

        if (landed) {
          // Part of the pack is in the cart. Clear the slots and show them.
          this.slots = new Array(this.max).fill(null);
          this.render();
          this.openCart();
        } else {
          // Nothing changed, so keep their picks and let them try again.
          this.render();
          this.refreshCart(false);
        }
        return;
      }

      this.slots = new Array(this.max).fill(null);
      this.render();
      this.openCart();
    } catch (error) {
      this.showError(this.strings.generic_error || 'Something went wrong. Please try again.');
      this.refreshCart(false);
    } finally {
      this.busy = false;
      this.addBtn.classList.remove('btn--loading');
      this.render();
    }
  }

  /**
   * The Ajax cart API does not use one error shape. Depending on the request
   * it answers with {description}, {message}, {errors: "text"} or
   * {errors: {field: [...]}}. Read all of them before falling back, otherwise
   * a useful message like "Only 1 item was added due to availability" gets
   * replaced by a generic please-try-again that sends people round in circles.
   */
  errorMessage(data) {
    const fallback = this.strings.generic_error || 'Something went wrong. Please try again.';
    if (!data || typeof data !== 'object') return fallback;

    if (typeof data.description === 'string' && data.description.trim()) return data.description;
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    if (typeof data.errors === 'string' && data.errors.trim()) return data.errors;

    if (data.errors && typeof data.errors === 'object') {
      const flattened = Object.keys(data.errors)
        .map((key) => [].concat(data.errors[key]).join(' '))
        .filter(Boolean)
        .join(' ');
      if (flattened.trim()) return flattened;
    }

    return fallback;
  }

  async cartCount() {
    try {
      const response = await fetch(this.routes.cart_url + '.js', {
        headers: { Accept: 'application/json' }
      });
      const cart = await response.json();
      return typeof cart.item_count === 'number' ? cart.item_count : null;
    } catch (e) {
      return null;
    }
  }

  refreshCart(open) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: Boolean(open) } }));
  }

  openCart() {
    const cartType = (window.FoxTheme && FoxTheme.settings && FoxTheme.settings.cartType) || 'drawer';

    if (cartType === 'page' || !document.querySelector('cart-drawer')) {
      window.location.href = this.routes.cart_url;
      return;
    }

    this.refreshCart(true);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  /* ------------------------------------------------------------ plumbing */

  get routes() {
    const themeRoutes = (window.FoxTheme && FoxTheme.routes) || {};
    return {
      cart_add_url: themeRoutes.cart_add_url || '/cart/add',
      cart_url: themeRoutes.cart_url || '/cart'
    };
  }

  fetchConfig() {
    if (window.FoxTheme && FoxTheme.utils && typeof FoxTheme.utils.fetchConfig === 'function') {
      return FoxTheme.utils.fetchConfig();
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    };
  }

  money(cents) {
    try {
      if (window.FoxTheme && FoxTheme.Currency && typeof FoxTheme.Currency.formatMoney === 'function') {
        return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
      }
    } catch (e) {
      /* fall through to the plain format below */
    }
    return (cents / 100).toFixed(2);
  }

  /**
   * The mobile tray is fixed to the bottom of the screen. Step aside when the
   * cart drawer opens so the two never fight over the same corner.
   */
  watchCartDrawer() {
    if (this.drawerObserver) this.drawerObserver.disconnect();

    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !('MutationObserver' in window)) return;

    const sync = () => {
      const open = drawer.hasAttribute('active') || drawer.hasAttribute('open');
      this.classList.toggle('hat-mix--drawer-open', open);
    };

    this.drawerObserver = new MutationObserver(sync);
    this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['active', 'open'] });
    sync();
  }

  /**
   * Keep the last row of hats clear of the fixed mobile tray.
   */
  watchTrayHeight() {
    if (this.trayObserver) this.trayObserver.disconnect();
    if (!this.tray || !('ResizeObserver' in window)) return;

    const apply = () => {
      this.style.setProperty('--hat-tray-height', this.tray.offsetHeight + 'px');
    };

    this.trayObserver = new ResizeObserver(apply);
    this.trayObserver.observe(this.tray);
    apply();
  }
}

if (!customElements.get('hat-mix-builder')) {
  customElements.define('hat-mix-builder', HatMixBuilder);
}